import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SEO, createPersonSchema, createBreadcrumbSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTeacherDetails } from '@/hooks/use-public-api';
import { useTeacherReviews } from '@/hooks/use-review-api';
import { specialtyLabel } from '@/lib/specialties';
import { useCurrency } from '@/contexts/CurrencyContext';
import { motion } from 'framer-motion';
import EmptyState from '@/components/common/EmptyState';
import AvailabilityCalendar from '@/components/booking/AvailabilityCalendar';
import {
  Star,
  Clock,
  Users,
  BookOpen,
  GraduationCap,
  Globe,
  Calendar,
  Award,
  Play,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  MapPin,
} from 'lucide-react';


const RatingBar = ({ stars, count, total }: { stars: number; count: number; total: number }) => (
  <div className="flex items-center gap-2 text-sm">
    <span className="w-6 text-end text-muted-foreground">{stars}</span>
    <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" aria-hidden="true" />
    <Progress value={total > 0 ? (count / total) * 100 : 0} className="h-2 flex-1" />
    <span className="w-8 text-end text-muted-foreground text-xs">{count}</span>
  </div>
);

const ReviewCard = ({ review, isRTL }: { review: any; isRTL: boolean }) => {
  const { t } = useLanguage();
  const ariaLabel = t('teacherProfile.ratingAriaLabel').replace('{rating}', String(review.rating));
  return (
    <div className="border-b border-border last:border-0 py-4 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          {review.student_image ? (
            <img src={review.student_image} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <span className="text-sm font-semibold text-primary">
              {(review.student_name || '?').charAt(0)}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-foreground">{review.student_name}</span>
            <span className="text-xs text-muted-foreground">{review.creation?.split(' ')[0]}</span>
          </div>
          <div
            className="flex items-center gap-0.5 mb-2"
            aria-label={ariaLabel}
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                aria-hidden="true"
                className={`h-3 w-3 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`}
              />
            ))}
            <span className="sr-only">{review.rating} {t('teacherProfile.outOfStars')}</span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {isRTL ? (review.comment_ar || review.comment) : review.comment}
          </p>
          {review.teacher_response && (
            <div className="mt-3 ps-4 border-s-2 border-primary/20">
              <p className="text-xs font-medium text-primary mb-1">
                {t('teacherProfile.teacherResponse')}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRTL ? (review.teacher_response_ar || review.teacher_response) : review.teacher_response}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TeacherProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isRTL, t, language } = useLanguage();
  const { formatPrice } = useCurrency();
  const { isAuthenticated } = useAuth();
  const [reviewPage, setReviewPage] = useState(0);

  const { data: teacher, isLoading } = useTeacherDetails(id || '');
  const { data: reviewsData } = useTeacherReviews(id || '', reviewPage);

  // Slug-derived fallbacks so the prerender + first-paint crawlers see a real
  // <title>/canonical even before the data fetch resolves. The success path
  // below replaces these with rich data once `teacher` is available.
  const slugTitle = (id || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const fallbackTeacherUrl = `https://hudanlilmuttaqeen.com/teachers/${id || ''}`;

  if (isLoading) {
    return (
      <Layout>
        <SEO
          title={`${slugTitle || t('nav.teachers')} | Hudan Lil Muttaqeen`}
          description={t('teacherProfile.certifiedQuranTeacher')}
          url={fallbackTeacherUrl}
          type="profile"
        />
        <div className="container py-12" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="h-64 bg-muted/50 rounded-2xl animate-pulse" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-32 bg-muted/50 rounded-xl animate-pulse" />
                <div className="h-48 bg-muted/50 rounded-xl animate-pulse" />
              </div>
              <div className="h-72 bg-muted/50 rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!teacher) {
    return (
      <Layout>
        <SEO
          title={`${t('teacherProfile.notFound.title')} | Hudan Lil Muttaqeen`}
          description={t('teacherProfile.notFound.desc')}
          url={fallbackTeacherUrl}
        />
        <div className="container py-20">
          <EmptyState
            icon={Users}
            title={t('teacherProfile.notFound.title')}
            description={t('teacherProfile.notFound.desc')}
            actionLabel={t('browseTeachers')}
            actionHref="/teachers"
          />
        </div>
      </Layout>
    );
  }

  const td = teacher as any;
  const name = isRTL && td.full_name_ar ? td.full_name_ar : td.full_name;
  const bio = isRTL && td.bio_ar ? td.bio_ar : td.bio;
  const specialties = td.specialties_list || (td.specialties ? td.specialties.split(',').map((s: string) => s.trim()) : []);
  const languages = td.languages_list || (td.teaching_languages ? td.teaching_languages.split(',').map((s: string) => s.trim()) : []);
  const availability = (td.availability as any[]) || [];
  const courses = (td.courses as any[]) || [];
  const reviews = reviewsData?.reviews || [];
  const totalReviews = reviewsData?.total || td.total_reviews || 0;
  const ratingDist = reviewsData?.rating_distribution || {};

  const teacherProfileUrl = `https://hudanlilmuttaqeen.com/teachers/${(td as any).slug || td.name}`;
  const teacherStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createPersonSchema({
        name,
        description: bio || '',
        image: td.profile_image || undefined,
        expertise: specialties,
        url: teacherProfileUrl,
        alternateName: isRTL
          ? (td.full_name || undefined)
          : (td.full_name_ar || undefined),
        inLanguage: language === 'ar' ? 'ar' : 'en',
      }),
      createBreadcrumbSchema(
        [
          { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
          { name: t('nav.teachers'), url: 'https://hudanlilmuttaqeen.com/teachers' },
          { name, url: teacherProfileUrl },
        ],
        language === 'ar' ? 'ar' : 'en'
      ),
    ],
  };

  const teacherSeoTitle = `${name} — ${t('teacherProfile.quranTeacherTitle')} | Hudan Lil Muttaqeen`;
  const teacherSeoDescription = (bio
    ? bio.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 155)
    : t('teacherProfile.certifiedQuranTeacher'));

  return (
    <Layout>
      <SEO
        title={teacherSeoTitle}
        description={teacherSeoDescription}
        image={td.profile_image || undefined}
        type="profile"
        url={teacherProfileUrl}
        structuredData={teacherStructuredData}
      />
      <div className="container py-8 max-w-5xl" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Back link */}
        <Link
          to="/teachers"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {t('teacherProfile.backToTeachers')}
        </Link>

        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="overflow-hidden mb-8">
            <div className="h-32 bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/10 relative">
              <div className="absolute inset-0 islamic-pattern-overlay opacity-[0.05]" />
            </div>
            <CardContent className="pt-0 pb-6 relative">
              <div className="flex flex-col sm:flex-row gap-5 -mt-12">
                {/* Avatar */}
                <div className="w-24 h-24 rounded-2xl border-4 border-background bg-muted overflow-hidden shrink-0 shadow-lg">
                  {td.profile_image ? (
                    <img src={td.profile_image} alt={name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10">
                      <span className="text-3xl font-arabic font-bold text-primary">{name.charAt(0)}</span>
                    </div>
                  )}
                </div>

                <div className="flex-1 pt-2 sm:pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div>
                      <h1 className="text-2xl font-bold text-foreground mb-1">{name}</h1>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                        {td.country && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {td.country}
                          </span>
                        )}
                        {td.years_experience && (
                          <span className="flex items-center gap-1">
                            <Award className="h-3.5 w-3.5" />
                            {td.years_experience} {t('teacherProfile.yearsExp')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isAuthenticated ? (
                        <Button asChild>
                          <Link to={`/student/booking?teacher=${td.name}`}>
                            <Calendar className="h-4 w-4 me-2" />
                            {t('bookSession')}
                          </Link>
                        </Button>
                      ) : (
                        <Button asChild>
                          <Link to="/login">
                            {t('teacherProfile.loginToBook')}
                          </Link>
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-5 mt-4 flex-wrap">
                    <div
                      className="flex items-center gap-1.5"
                      aria-label={t('teacherProfile.ratingReviewsAriaLabel')
                        .replace('{rating}', (td.average_rating || 0).toFixed(1))
                        .replace('{count}', String(totalReviews))}
                    >
                      <Star className="h-4 w-4 text-amber-500 fill-amber-500" aria-hidden="true" />
                      <span aria-hidden="true" className="font-semibold text-foreground">{(td.average_rating || 0).toFixed(1)}</span>
                      <span aria-hidden="true" className="text-sm text-muted-foreground">({totalReviews})</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {td.students_taught || td.total_students || 0} {t('teacherProfile.students')}
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <BookOpen className="h-4 w-4" />
                      {td.total_sessions || 0} {t('teacherProfile.sessions')}
                    </div>
                    <div className="text-lg font-bold text-primary">
                      {formatPrice(td.hourly_rate || 0)}
                      <span className="text-sm font-normal text-muted-foreground ms-1">/{t('teacherProfile.hr')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card>
                <CardContent className="pt-5">
                  <h2 className="text-lg font-semibold text-foreground mb-3">
                    {t('teacherProfile.about')}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                    {bio || t('teacherProfile.noBio')}
                  </p>

                  {/* Video intro */}
                  {td.video_intro_url && (
                    <a
                      href={td.video_intro_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
                    >
                      <Play className="h-4 w-4" />
                      {t('teacherProfile.watchVideo')}
                    </a>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Specialties & Languages */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <Card>
                <CardContent className="pt-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-primary" />
                        {t('specialties')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {specialties.length > 0 ? specialties.map((s: string) => (
                          <Badge key={s} variant="secondary" className="text-xs">
                            {specialtyLabel(s, isRTL)}
                          </Badge>
                        )) : (
                          <span className="text-sm text-muted-foreground">{t('teacherProfile.notSpecified')}</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Globe className="h-4 w-4 text-primary" />
                        {t('teachingLanguages')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {languages.length > 0 ? languages.map((l: string) => (
                          <Badge key={l} variant="outline" className="text-xs">
                            {l}
                          </Badge>
                        )) : (
                          <span className="text-sm text-muted-foreground">{t('teacherProfile.notSpecified')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Courses */}
            {courses.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h2 className="text-lg font-semibold text-foreground mb-4">
                      {t('nav.courses')} ({courses.length})
                    </h2>
                    <div className="space-y-3">
                      {courses.map((course: any) => (
                        <Link
                          key={course.name || course.slug}
                          to={`/courses/${course.slug || course.name}`}
                          className="flex items-center gap-4 p-3 rounded-xl hover:bg-muted/50 transition-colors group"
                        >
                          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <BookOpen className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
                              {isRTL ? (course.title_ar || course.title) : course.title}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {course.level} &middot; {course.total_lessons || 0} {t('teacherProfile.lessons')}
                            </p>
                          </div>
                          <div className="text-sm font-semibold text-foreground">
                            {course.is_free ? t('teacherProfile.free') : formatPrice(course.effective_price || course.price)}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Reviews */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <Card>
                <CardContent className="pt-5">
                  <h2 className="text-lg font-semibold text-foreground mb-4">
                    {t('teacherProfile.reviews')} ({totalReviews})
                  </h2>

                  {/* Rating summary */}
                  {totalReviews > 0 && (
                    <div className="flex items-start gap-8 mb-6 pb-6 border-b border-border">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-foreground">{(td.average_rating || 0).toFixed(1)}</div>
                        <div
                          className="flex items-center gap-0.5 mt-1 justify-center"
                          aria-label={t('teacherProfile.averageRatingAriaLabel')
                            .replace('{rating}', (td.average_rating || 0).toFixed(1))}
                        >
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              aria-hidden="true"
                              className={`h-3.5 w-3.5 ${i < Math.round(td.average_rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`}
                            />
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{totalReviews} {t('teacherProfile.reviewsCount')}</div>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {[5, 4, 3, 2, 1].map((stars) => (
                          <RatingBar
                            key={stars}
                            stars={stars}
                            count={ratingDist[stars] || 0}
                            total={totalReviews}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Review list */}
                  {reviews.length > 0 ? (
                    <div className="space-y-0">
                      {reviews.map((review: any) => (
                        <ReviewCard key={review.name} review={review} isRTL={isRTL} />
                      ))}

                      {totalReviews > 10 && (
                        <div className="flex items-center justify-center gap-3 pt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={reviewPage === 0}
                            onClick={() => setReviewPage((p) => Math.max(0, p - 1))}
                          >
                            {t('previous')}
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            {reviewPage + 1} / {Math.ceil(totalReviews / 10)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={(reviewPage + 1) * 10 >= totalReviews}
                            onClick={() => setReviewPage((p) => p + 1)}
                          >
                            {t('next')}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      {t('teacherProfile.noReviews')}
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Sidebar */}
          {/* TODO(a11y): consider hoisting booking CTA above review list for keyboard focus order (M-07) */}
          <div className="space-y-6">
            {/* Booking CTA */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="border-primary/20 bg-primary/[0.02]">
                <CardContent className="pt-5">
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-foreground">
                      {formatPrice(td.hourly_rate || 0)}
                      <span className="text-sm font-normal text-muted-foreground ms-1">/{t('teacherProfile.hr')}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('teacherProfile.trialAvailable')}
                    </p>
                  </div>
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <Button asChild className="w-full">
                        <Link to={`/student/booking?teacher=${td.name}`}>
                          <Calendar className="h-4 w-4 me-2" />
                          {t('bookSession')}
                        </Link>
                      </Button>
                      <Button variant="outline" asChild className="w-full">
                        <Link to={`/student/chat?to=${td.name}`}>
                          <MessageCircle className="h-4 w-4 me-2" />
                          {t('teacherProfile.sendMessage')}
                        </Link>
                      </Button>
                    </div>
                  ) : (
                    <Button asChild className="w-full">
                      <Link to="/register/student">
                        {t('teacherProfile.signupToBook')}
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Availability: live bookable slots in the viewer's timezone */}
            {availability.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-primary" />
                      {t('teacherProfile.availability')}
                    </h3>
                    <AvailabilityCalendar
                      teacher={(td as any).slug || td.name}
                      onSelectSlot={(iso) =>
                        navigate(
                          `/student/booking?teacher=${encodeURIComponent(td.name)}&slot=${encodeURIComponent(iso)}`
                        )
                      }
                    />
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Quick Info */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card>
                <CardContent className="pt-5 space-y-3">
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    {t('teacherProfile.quickInfo')}
                  </h3>
                  <div className="flex items-center gap-3 text-sm">
                    <Award className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className="text-muted-foreground">
                      {t('teacherProfile.ijazahCertified')}
                    </span>
                  </div>
                  {td.years_experience && (
                    <div className="flex items-center gap-3 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">
                        {td.years_experience} {t('teacherProfile.yearsExperience')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground">
                      {td.students_taught || td.total_students || 0} {t('teachers.studentsTaught')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground">
                      {td.total_sessions || 0} {t('teacherProfile.sessionsCompleted')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TeacherProfilePage;
